/**
 * Client-side validation for the company registration form (mirrors backend
 * RegisterCompanyRequest + Password::defaults: min 10, mixed case, number, symbol).
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Unicode letters / marks, spaces, hyphen, apostrophe (matches backend name regex). */
export const PERSON_NAME_RE = /^[\p{L}\p{M}\s\-.'’]+$/u;

/** Company names: letters, numbers, common punctuation (matches backend). */
const COMPANY_NAME_RE = /^[\p{L}\p{N}\p{M}\s\-.&'’,()/]+$/u;

const MAX_LEN = 255;
const MIN_PASSWORD = 10;

/**
 * @param {string} password
 * @returns {string|null} error message or null if ok
 */
/**
 * Optional display name (e.g. accept-invite): empty ok, else min 2 chars + pattern.
 * @returns {{ ok: true, value: string|undefined } | { ok: false, error: string }}
 */
export function validateOptionalDisplayName(raw) {
  const t = (raw ?? '').trim();
  if (t === '') {
    return { ok: true, value: undefined };
  }
  if (t.length < 2) {
    return {
      ok: false,
      error: 'Use at least 2 characters for your name, or leave it blank.',
    };
  }
  if (t.length > 255) {
    return { ok: false, error: 'Name is too long.' };
  }
  if (!PERSON_NAME_RE.test(t)) {
    return {
      ok: false,
      error: 'Use letters, spaces, hyphens, or apostrophes only.',
    };
  }
  return { ok: true, value: t };
}

export function validatePasswordStrength(password) {
  if (!password) {
    return 'Password is required.';
  }
  if (password.length < MIN_PASSWORD) {
    return `Use at least ${MIN_PASSWORD} characters.`;
  }
  if (password.length > 500) {
    return 'Password is too long.';
  }
  if (!/[a-z]/.test(password)) {
    return 'Include at least one lowercase letter.';
  }
  if (!/[A-Z]/.test(password)) {
    return 'Include at least one uppercase letter.';
  }
  if (!/[0-9]/.test(password)) {
    return 'Include at least one number.';
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    return 'Include at least one symbol (e.g. !@#$%).';
  }
  return null;
}

/**
 * @param {object} raw
 * @returns {{ valid: boolean, fieldErrors: Record<string, string>, values: object }}
 */
export function validateRegisterCompanyForm(raw) {
  const company_name = (raw.company_name ?? '').trim();
  const first_name = (raw.first_name ?? '').trim();
  const last_name = (raw.last_name ?? '').trim();
  const email = (raw.email ?? '').trim().toLowerCase();
  const password = raw.password ?? '';
  const password_confirmation = raw.password_confirmation ?? '';

  /** @type {Record<string, string>} */
  const fieldErrors = {};

  if (!company_name) {
    fieldErrors.company_name = 'Company name is required.';
  } else if (company_name.length < 2) {
    fieldErrors.company_name = 'Enter at least 2 characters.';
  } else if (company_name.length > MAX_LEN) {
    fieldErrors.company_name = `Use at most ${MAX_LEN} characters.`;
  } else if (!COMPANY_NAME_RE.test(company_name)) {
    fieldErrors.company_name =
      'Use letters, numbers, and common punctuation only.';
  }

  if (!first_name) {
    fieldErrors.first_name = 'First name is required.';
  } else if (first_name.length < 2) {
    fieldErrors.first_name = 'Enter at least 2 characters.';
  } else if (first_name.length > MAX_LEN) {
    fieldErrors.first_name = `Use at most ${MAX_LEN} characters.`;
  } else if (!PERSON_NAME_RE.test(first_name)) {
    fieldErrors.first_name = 'Use letters, spaces, hyphens, or apostrophes only.';
  }

  if (!last_name) {
    fieldErrors.last_name = 'Last name is required.';
  } else if (last_name.length < 2) {
    fieldErrors.last_name = 'Enter at least 2 characters.';
  } else if (last_name.length > MAX_LEN) {
    fieldErrors.last_name = `Use at most ${MAX_LEN} characters.`;
  } else if (!PERSON_NAME_RE.test(last_name)) {
    fieldErrors.last_name = 'Use letters, spaces, hyphens, or apostrophes only.';
  }

  if (!email) {
    fieldErrors.email = 'Work email is required.';
  } else if (email.length > MAX_LEN) {
    fieldErrors.email = `Use at most ${MAX_LEN} characters.`;
  } else if (!EMAIL_RE.test(email)) {
    fieldErrors.email = 'Enter a valid email address.';
  }

  const pwdErr = validatePasswordStrength(password);
  if (pwdErr) {
    fieldErrors.password = pwdErr;
  }

  if (!password_confirmation) {
    fieldErrors.password_confirmation = 'Confirm your password.';
  } else if (password !== password_confirmation) {
    fieldErrors.password_confirmation = 'Passwords do not match.';
  }

  const valid = Object.keys(fieldErrors).length === 0;

  const fullName = [first_name, last_name].filter(Boolean).join(' ').trim();

  return {
    valid,
    fieldErrors,
    values: {
      company_name,
      first_name,
      last_name,
      email,
      password,
      password_confirmation,
      /** Combined display name for API `name` (register-company). */
      name: fullName,
    },
  };
}
